import { useState, useCallback } from 'react';
import { DocumentVisibility } from '../../api/data-contracts';
import { SharingFormData } from '../../components/ui/SharingControls';
import { FolderSharingState } from '../../components/dialogs/ShareFolderDialog/types';

/**
 * Custom hook for managing folder sharing form state
 * Handles all form field changes and file selection
 *
 * @returns Form state and handlers
 */
export const useShareFolderForm = (
  initialState: FolderSharingState
) => {
  const [formState, setFormState] = useState<FolderSharingState>(initialState);

  /**
   * Handle URL change
   */
  const handleUrlChange = useCallback((url: string) => {
    setFormState(prev => ({ ...prev, url }));
  }, []);

  /**
   * Handle name change
   */
  const handleNameChange = useCallback((name: string) => {
    setFormState(prev => ({ ...prev, name }));
  }, []);

  /**
   * Handle description change
   */
  const handleDescriptionChange = useCallback((description: string) => {
    setFormState(prev => ({ ...prev, description }));
  }, []);

  /**
   * Handle sharing data change
   */
  const handleSharingChange = useCallback((updates: Partial<SharingFormData>) => {
    setFormState(prev => ({
      ...prev,
      sharingData: { ...prev.sharingData, ...updates }
    }));
  }, []);

  /**
   * Toggle file selection
   */
  const handleFileToggle = useCallback((fileId: string) => {
    setFormState(prev => ({
      ...prev,
      selectedFiles: prev.selectedFiles.includes(fileId)
        ? prev.selectedFiles.filter(id => id !== fileId)
        : [...prev.selectedFiles, fileId],
    }));
  }, []);

  /**
   * Select all files
   */
  const handleSelectAll = useCallback(() => {
    setFormState(prev => {
      if (prev.files) {
        return {
          ...prev,
          selectedFiles: prev.files.map(f => f.id)
        };
      }
      return prev;
    });
  }, []);

  /**
   * Deselect all files
   */
  const handleSelectNone = useCallback(() => {
    setFormState(prev => ({ ...prev, selectedFiles: [] }));
  }, []);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setFormState(initialState);
  }, [initialState]);

  return {
    formState,
    handleUrlChange,
    handleNameChange,
    handleDescriptionChange,
    handleSharingChange,
    handleFileToggle,
    handleSelectAll,
    handleSelectNone,
    setFormState,
    resetForm,
  };
};
